import 'whatwg-fetch'
import 'intersection-observer'
import 'ie-array-find-polyfill'
import 'core-js/features/array/from'
import 'core-js/features/array/includes'

import { getGPUTier } from 'detect-gpu'
import Highway from '@dogstudio/highway'
import Layzr from 'layzr.js'
import gsap from 'gsap'
import { throttle, debounce } from 'throttle-debounce'
import locomotiveScroll from 'locomotive-scroll'
import Loader from './util/Loader'
import quicklink from 'quicklink/dist/quicklink.mjs'

// Utils
import Menu from './util/Menu'
import IE11PolyFills from './util/IE11PolyFills'
import Background from './util/Background'
import Cursor from './util/Cursor'

// Routes
import Home from './routes/Home'
import Page from './routes/Page'

// Transitions
import Fade from './transitions/Fade'
import MenuTransition from './transitions/MenuTransition'

export default class App {

  /**
    * Loads the page
    * then calls `this.start()` when DOM is ready
    */
    constructor() {
        this.iePolyfills = new IE11PolyFills()
        this.resize = this.resize.bind(this)
        this.scroll = this.scroll.bind(this)
        this.update = this.update.bind(this)

        App.currentScroll = 0

        this.resizeDebounced = debounce(100, this.resize)
        this.resizeThrottled = throttle(150, this.resize)
        this.scrollDebounced = debounce(100, this.scroll)
        this.scrollThrottled = throttle(50, this.scroll)

        Loader.load(document.body).then(() => {
            this.start()
        })
    }

  /**
    * Inits everything that is app-wide
    * ie: Menu, scroll / resize events...
    *
    * @returns {void}
    */
    start() {
        this.checkMobile()
        App.menu = new Menu()
        App.cursor = new Cursor()
        App.background = new Background()
        App.plane = null
        this.launchLazyLoad()
        this.detectGPU()
        this.initSmoothScroll()

        if (App.GPU.tier !== 'GPU_DESKTOP_TIER_0') document.body.classList.add('webgl')

        this.initHighway().then(() => {
            this.events()
            App.cursor.checkLinks(this.currentRenderer.view)

            this.transitionElt = document.querySelector('.transition')

            gsap.to(this.transitionElt, {
                y: '100vh',
                duration: 1,
                ease: 'power3.inOut',
                onComplete: () => {
                    gsap.set(this.transitionElt, {
                        y: 0,
                        scaleY: 0
                    })
                },
                onStart: () => {
                    window.scrollTo(0, 0)
                    requestAnimationFrame(this.update)
                }
            })
        })
    }

    launchLazyLoad() {
        /* eslint-disable-next-line */
        this.lazyLoad = Layzr()
        
        this.lazyLoad.on('src:after', (element) => {
            element.classList.add('show')
        })

        this.lazyLoad
            .update()
            .check()
    }

    detectGPU() {
        App.GPU = getGPUTier({
            mobileBenchmarkPercentages: [0, 0, 0, 60],
            desktopBenchmarkPercentages: [0, 35, 30, 25],
            failIfMajorPerformanceCaveat: true,
            forceMobile: false
        })
    }

  /**
    * Starts an Highway instance
    * with our Views and Transitions
    *
    * @returns {void}
    */
    initHighway() {
    return new Promise((resolve) => {
        this.H = new Highway.Core({
            renderers: {
                home: Home,
                page: Page
            },
            transitions: {
                default: Fade,
                contextual: {
                    menu: MenuTransition
                }
            }
        })

        this.setCurrentRenderer()
            .then(resolve)
        })
    }

    checkMobile() {
        App.isMobile && document.body.classList.remove('isMobile')
        App.isMobile && document.documentElement.classList.remove('isMobile')
        /* eslint-disable */
        App.isMobile = (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent))
        App.isMobile && document.body.classList.add('isMobile')
        App.isMobile && document.documentElement.classList.add('isMobile')
    }

    initSmoothScroll() {
        /* eslint-disable-next-line */
        const inertia = (App.GPU.tier === 'GPU_DESKTOP_TIER_0') ? 1 : 0.8

        App.smoothScroll = new locomotiveScroll({
            el: document.body.querySelector('.js-scroll'),
            smooth: true,
            passive: true,
            inertia
        })
    }

    setCurrentRenderer() {
        return new Promise((resolve) => {
            this.H.properties.renderer.then(() => {
                this.currentRenderer = this.H.To ? this.H.To : this.H.From

                resolve(this.currentRenderer)
            })
        })
    }

    resize() {
        this.checkMobile()
        this.currentRenderer.resize()
        App.background.resize()
        App.menu && App.menu.resize()
    }

    scroll(e) {
        App.currentScroll = App.smoothScroll ? e.scroll.y : window.pageYOffset

        this.lazyLoad.check()
        this.currentRenderer.scroll()
        App.background.scroll()
        App.menu && App.menu.scroll()
    }

	update() {
        this.currentRenderer.update()
        requestAnimationFrame(this.update)
	}

    events() {
        window.addEventListener('resize', this.resizeThrottled)
        window.addEventListener('resize', this.resizeDebounced)
        App.smoothScroll && App.smoothScroll.on('scroll', this.scrollThrottled)
        App.smoothScroll && App.smoothScroll.on('scroll', this.scrollDebounced)
        window.addEventListener('orientationchange', this.resize)

        this.H.on('NAVIGATE_OUT', () => {
            App.cursor.resetScale()
        })

        this.H.on('NAVIGATE_IN', () => {
            this.setCurrentRenderer()
            .then((renderer) => {
                document.title = renderer.properties.page.title
            })
        })

        this.H.on('NAVIGATE_END', ({ to, location }) => {
            this.checkAnchor(location)
            App.smoothScroll && App.smoothScroll.on('scroll', this.scrollThrottled)
            App.smoothScroll && App.smoothScroll.on('scroll', this.scrollDebounced)
            App.cursor.checkLinks(to.view)
            App.menu.onPageChange(location)
            quicklink({ el: to.view })
            this.lazyLoad
                .update()
                .check()
        })

        window.addEventListener('highwayredirect', (e) => {
            this.H.redirect(e.detail.url)
        })
    }

    checkAnchor(location) {
        if (location.anchor) {
            const el = document.querySelector(location.anchor)

            if (el) window.scrollTo(el.offsetLeft, el.offsetTop)
        }
    }
}



// WEBPACK FOOTER //
// ./scripts/App.js